import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyOrangeMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  const bgGradient = (
    <defs>
      <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#FF6600", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#FF7900", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#000000", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
  );

  // Signal grids - communication network lattice
  const signalGrids: JSX.Element[] = [];
  const gridSize = 20;
  const cellSize = 38;
  
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      const x = -380 + i * cellSize;
      const y = -380 + j * cellSize;
      const isActive = Math.sin(i * 0.5) * Math.cos(j * 0.5) > 0;
      
      signalGrids.push(
        <circle
          key={`signal-${i}-${j}`}
          cx={x}
          cy={y}
          r={8}
          fill={isActive ? "#FF7900" : "none"}
          stroke="#FFFFFF"
          strokeWidth={1.5}
          opacity={isActive ? 0.4 : 0.15}
        />
      );
    }
  }

  // Wave propagation - radio frequency waves
  const waveLines: JSX.Element[] = [];
  for (let wave = 0; wave < 16; wave++) {
    const points: string[] = [];
    const y = -480 + wave * 60;
    
    for (let p = 0; p <= 40; p++) {
      const t = p / 40;
      const x = -360 + t * 720;
      const yOffset = Math.sin(t * Math.PI * 4 + wave * 0.6) * 30;
      points.push(`${x},${y + yOffset}`);
    }
    
    waveLines.push(
      <polyline
        key={`wave-${wave}`}
        points={points.join(" ")}
        fill="none"
        stroke="#FF7900"
        strokeWidth={2}
        opacity={0.25}
      />
    );
  }

  // Warm spectrum fields - connectivity zones
  const spectrumFields: JSX.Element[] = [];
  for (let field = 0; field < 10; field++) {
    const radius = 80 + field * 35;
    
    spectrumFields.push(
      <circle
        key={`spectrum-${field}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={field % 3 === 0 ? 2.5 : 1.5}
        opacity={0.3 - field * 0.02}
      />
    );
  }

  // Network nodes
  const networkNodes: JSX.Element[] = [];
  const nodeRings = [140, 220, 300];
  
  nodeRings.forEach((r, ri) => {
    const count = 8 + ri * 4;
    for (let n = 0; n < count; n++) {
      const angle = (n / count) * 2 * Math.PI;
      const nx = Math.cos(angle) * r;
      const ny = Math.sin(angle) * r;
      
      networkNodes.push(
        <g key={`node-${ri}-${n}`}>
          <circle
            cx={nx}
            cy={ny}
            r={28 - ri * 5}
            fill="none"
            stroke="#FF7900"
            strokeWidth={2.5}
            opacity={0.5}
          />
          <circle
            cx={nx}
            cy={ny}
            r={15 - ri * 3}
            fill="#FF6600"
            opacity={0.4}
          />
        </g>
      );
    }
  });

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#orangeGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {signalGrids}
        {waveLines}
        {spectrumFields}
        {networkNodes}
        <circle cx={0} cy={0} r={50} fill="none" stroke="#FFFFFF" strokeWidth={3} opacity={0.6} />
        <circle cx={0} cy={0} r={35} fill="#FF7900" opacity={0.4} />
      </g>
    </svg>
  );
}
